// src/components/Layout.js
import React, { useEffect, useState, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import Sidebar from './Sidebar'
import { Box, CircularProgress, Toolbar } from '@mui/material'

const drawerWidth = 240
const ROLE_CACHE_KEY = 'bbm:userRole'

export default function Layout() {
  const navigate = useNavigate()
  const [bootLoading, setBootLoading] = useState(true)     // only for first session check
  const [userRole, setUserRole] = useState(
    // use cached role for instant paint; Sidebar updates when fresh role arrives
    localStorage.getItem(ROLE_CACHE_KEY) || 'user'
  )

  const setRole = (r) => {
    setUserRole(r)
    localStorage.setItem(ROLE_CACHE_KEY, r)
  }

  const computeRole = useCallback(async (uid) => {
    if (!uid) { setRole('user'); return }
    const { data, error } = await supabase
      .from('approved_users')
      .select('role, is_approved, expires_at')
      .eq('user_id', uid)
      .maybeSingle()

    if (error) {
      console.warn('[Layout] role fetch error:', error)
      setRole('user')
      return
    }
    const nowIso = new Date().toISOString()
    const isAdmin = data?.role === 'admin' && data?.is_approved === true &&
                    (!data?.expires_at || data.expires_at >= nowIso)
    setRole(isAdmin ? 'admin' : 'user')
  }, [])

  useEffect(() => {
    

    const init = async () => {
      // Faster than getUser() for initial check
      const { data: sessionData } = await supabase.auth.getSession()
      const session = sessionData?.session
      const currentUser = session?.user ?? null

      if (!currentUser) {
        setBootLoading(false)
        navigate('/login', { replace: true })
        return
      }

      // ✅ Don’t block UI on role — render immediately
      setBootLoading(false)
      // Refresh role in background
      computeRole(currentUser.id)
    }
    init()

    // Listen for sign-in/sign-out
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const nextUser = session?.user ?? null
      if (!nextUser) {
        setRole('user')
        navigate('/login', { replace: true })
        return
      }
      // Don’t block UI; update role when ready
      computeRole(nextUser.id)
    })

    return () => sub?.subscription?.unsubscribe?.()
  }, [navigate, computeRole])

  const handleLogout = async () => {
    setRole('user') // clear fast to avoid UI flash
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  if (bootLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar onLogout={handleLogout} userRole={userRole} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          ml: { xs: 0, md: `${drawerWidth}px` },
          minHeight: '100vh',
          bgcolor: '#f6f7fb',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  )
}